import {StrictMode, useState, useEffect} from 'react';
import {createRoot} from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import { ToastProvider } from './ui/Toast';
import { BackgroundTaskBar } from './ui/BackgroundTaskBar';
import './index.css';

// Local-First API mode — all data flows through the Worker (local or remote).
const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';

/**
 * Critical Configuration Error screen — shown when the API is unreachable.
 * The app cannot fall back to localStorage; a real API is always required.
 */
function CriticalApiError({ apiUrl, retry }: { apiUrl: string; retry: () => void }) {
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-10 text-center space-y-6">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-black text-zinc-900">Critical Configuration Error</h1>
        <p className="text-sm text-zinc-500 leading-relaxed">
          The platform requires a running API server but could not connect to:
        </p>
        <code className="block bg-zinc-100 px-4 py-2.5 rounded-xl text-sm font-mono text-red-600 break-all">{apiUrl}/api/health</code>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left space-y-2">
          <p className="text-xs font-bold text-amber-800 uppercase tracking-widest">How to fix</p>
          <p className="text-xs text-amber-700"><strong>Local dev:</strong> Run <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono text-[11px]">npx wrangler dev --env local</code></p>
          <p className="text-xs text-amber-700"><strong>Remote:</strong> Set <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono text-[11px]">VITE_API_URL</code> to a valid API endpoint.</p>
        </div>
        <button
          onClick={retry}
          className="w-full px-6 py-3 bg-zinc-900 text-white rounded-xl text-sm font-bold hover:bg-zinc-800 transition-colors"
        >
          Retry Connection
        </button>
      </div>
    </div>
  );
}

/**
 * Root wrapper — performs an API health check before mounting the app.
 * If the API is unreachable, a full-screen error is shown with instructions.
 */
function Root() {
  const [status, setStatus] = useState<'checking' | 'ok' | 'error'>('checking');

  const check = () => {
    setStatus('checking');
    fetch(`${API_BASE}/api/health`, { method: 'GET' })
      .then(r => {
        if (r.ok) {
          console.info('[ClickIt] API connected ✓', API_BASE);
          setStatus('ok');
        } else {
          console.error('[ClickIt] API responded with', r.status);
          setStatus('error');
        }
      })
      .catch(() => {
        console.error('[ClickIt] Cannot reach API at', API_BASE);
        setStatus('error');
      });
  };

  useEffect(() => { check(); }, []);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
          <span className="text-sm text-zinc-500 font-medium">Connecting to API...</span>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return <CriticalApiError apiUrl={API_BASE} retry={check} />;
  }

  return (
    <BrowserRouter>
      <ToastProvider>
        <App migrationApplied={false} />
        <BackgroundTaskBar />
      </ToastProvider>
    </BrowserRouter>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
